/**
 * <gsf-globe>
 * Interactive cobe globe with editable city markers.
 * Reads markers from [data-marker] children, initializes cobe, then injects
 * tooltip elements as children of cobe-created anchor divs (so we don't depend
 * on CSS Anchor Positioning support, which is missing in Safari).
 */
class GsfGlobe extends HTMLElement {
  connectedCallback() {
    this._canvas = this.querySelector('[data-canvas]');
    this._markerSources = Array.from(this.querySelectorAll('[data-marker]'));
    if (!this._canvas || this._markerSources.length === 0) return;

    this._speed = parseFloat(this.dataset.speed);
    if (isNaN(this._speed)) this._speed = 0.003;
    this._initialPhi = parseFloat(this.dataset.initialPhi);
    if (isNaN(this._initialPhi)) this._initialPhi = 1.53;
    this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._instanceId = 'g' + Math.random().toString(36).slice(2, 8);

    this._buildMarkers();
    this._setupReveal();
    this._waitForCobe();
  }

  disconnectedCallback() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    if (this._updateInterval) clearInterval(this._updateInterval);
    if (this._sizeObserver) this._sizeObserver.disconnect();
    if (this._intersectionObserver) this._intersectionObserver.disconnect();
    if (this._globe && typeof this._globe.destroy === 'function') {
      try { this._globe.destroy(); } catch (e) { /* ignore */ }
    }
    if (this._onPointerMove) window.removeEventListener('pointermove', this._onPointerMove);
    if (this._onPointerUp) window.removeEventListener('pointerup', this._onPointerUp);
  }

  _buildMarkers() {
    this._markers = this._markerSources.map((el, idx) => {
      const lat = parseFloat(el.dataset.lat);
      const lng = parseFloat(el.dataset.lng);
      return {
        id: `${this._instanceId}-${idx}`,
        location: [isNaN(lat) ? 0 : lat, isNaN(lng) ? 0 : lng],
        size: 0.04,
        city: el.dataset.city || '',
        visitors: parseInt(el.dataset.visitors, 10) || 0,
        trend: parseInt(el.dataset.trend, 10) || 0,
        animate: el.dataset.animate === 'true',
      };
    });
  }

  _waitForCobe() {
    if (typeof window.createGlobe === 'function') {
      this._initGlobe();
      return;
    }
    let attempts = 0;
    const tick = () => {
      if (typeof window.createGlobe === 'function') {
        this._initGlobe();
        return;
      }
      if (attempts++ > 100) return; // ~5s max wait
      setTimeout(tick, 50);
    };
    tick();
  }

  _initGlobe() {
    const canvas = this._canvas;

    const tryInit = () => {
      const width = canvas.offsetWidth;
      if (width === 0) return false;
      this._startGlobe(width);
      return true;
    };

    if (!tryInit()) {
      const ro = new ResizeObserver((entries) => {
        if (entries[0]?.contentRect.width > 0) {
          ro.disconnect();
          tryInit();
        }
      });
      ro.observe(canvas);
    }
  }

  _startGlobe(initialWidth) {
    const canvas = this._canvas;
    this._phi = this._initialPhi;
    this._theta = 0.2;
    this._dragOffset = { phi: 0, theta: 0 };
    this._pointer = null;
    this._isPaused = false;
    this._anim = { phi: 0 };
    const isDark = this.closest('.gsf-globe--dark') !== null;

    this._globe = window.createGlobe(canvas, {
      devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      width: initialWidth,
      height: initialWidth,
      phi: this._phi,
      theta: this._theta,
      dark: isDark ? 1 : 0,
      diffuse: 1.5,
      mapSamples: 16000,
      mapBrightness: isDark ? 6 : 10,
      baseColor: isDark ? [0.3, 0.3, 0.3] : [1, 1, 1],
      markerColor: [0.77, 0.51, 0.16], // earth-500
      glowColor: isDark ? [0.6, 0.5, 0.3] : [0.94, 0.93, 0.91],
      markerElevation: 0,
      markers: this._markers.map((m) => ({ id: m.id, location: m.location, size: m.size })),
    });

    if (!this._globe || typeof this._globe.update !== 'function') return;

    this._setupTooltips();
    this._setupDrag();
    this._setupAutoUpdate();
    this._setupCanvasResize();
    this._setupViewportPause();
    this._animate();

    requestAnimationFrame(() => {
      canvas.dataset.loaded = 'true';
    });
  }

  _setupTooltips() {
    const attachOnce = () => {
      const wrapper = this._canvas.parentElement;
      if (!wrapper) return false;
      let attached = 0;
      for (const m of this._markers) {
        const anchor = this._findAnchor(wrapper, m.id);
        if (!anchor) continue;
        if (anchor.querySelector('.gsf-globe__tooltip')) {
          attached++;
          continue;
        }
        anchor.appendChild(this._createTooltip(m));
        attached++;
      }
      return attached === this._markers.length;
    };

    requestAnimationFrame(() => {
      if (!attachOnce()) {
        // Cobe may need a couple of frames before all anchors exist
        let retries = 0;
        const retry = () => {
          if (attachOnce() || retries++ > 10) return;
          requestAnimationFrame(retry);
        };
        retry();
      }
    });
  }

  _findAnchor(wrapper, id) {
    for (const child of wrapper.children) {
      if (child === this._canvas) continue;
      const styleStr = child.getAttribute('style') || '';
      if (styleStr.indexOf(`anchor-name:--cobe-${id}`) !== -1
        || styleStr.indexOf(`anchor-name: --cobe-${id}`) !== -1) {
        return child;
      }
    }
    return null;
  }

  _createTooltip(m) {
    const tooltip = document.createElement('div');
    tooltip.className = 'gsf-globe__tooltip';
    tooltip.dataset.markerId = m.id;
    // CSS-var trick: when --cobe-visible-{id} is defined ("N"), the property
    // becomes invalid → defaults (opacity 1, no blur). When undefined, fallback
    // makes opacity 0 / blur 8px. Cobe sets the var per frame.
    tooltip.style.opacity = `var(--cobe-visible-${m.id}, 0)`;
    tooltip.style.filter = `blur(calc((1 - var(--cobe-visible-${m.id}, 0)) * 8px))`;
    tooltip.innerHTML = `
      ${m.city ? `<span class="gsf-globe__tooltip-city">${this._escape(m.city)}</span>` : ''}
      <span class="gsf-globe__tooltip-visitors" data-visitors>${m.visitors}</span>
      <span class="gsf-globe__tooltip-trend" data-trend>${this._formatTrend(m.trend)}</span>
    `;
    this._setTrendClass(tooltip.querySelector('[data-trend]'), m.trend);
    return tooltip;
  }

  _formatTrend(t) {
    return `${t >= 0 ? '↑' : '↓'} ${Math.abs(t)}%`;
  }

  _setTrendClass(el, t) {
    if (!el) return;
    el.classList.remove('gsf-globe__tooltip-trend--up', 'gsf-globe__tooltip-trend--down');
    el.classList.add(t >= 0 ? 'gsf-globe__tooltip-trend--up' : 'gsf-globe__tooltip-trend--down');
  }

  _setupDrag() {
    this._canvas.addEventListener('pointerdown', (e) => {
      this._pointer = { x: e.clientX, y: e.clientY };
      this.dataset.grabbing = 'true';
      this._isPaused = true;
    });

    this._onPointerMove = (e) => {
      if (!this._pointer) return;
      this._dragOffset = {
        phi: (e.clientX - this._pointer.x) / 300,
        theta: (e.clientY - this._pointer.y) / 1000,
      };
    };
    this._onPointerUp = () => {
      if (this._pointer) {
        this._phi += this._dragOffset.phi;
        this._theta += this._dragOffset.theta;
        this._dragOffset = { phi: 0, theta: 0 };
      }
      this._pointer = null;
      this.removeAttribute('data-grabbing');
      this._isPaused = false;
    };
    window.addEventListener('pointermove', this._onPointerMove, { passive: true });
    window.addEventListener('pointerup', this._onPointerUp, { passive: true });
  }

  _setupAutoUpdate() {
    this._updateInterval = setInterval(() => {
      this._markers.forEach((m) => {
        if (!m.animate) return;
        m.visitors = Math.max(0, m.visitors + Math.floor(Math.random() * 11) - 3);
        m.trend = Math.max(-25, Math.min(25, m.trend + Math.floor(Math.random() * 5) - 2));
        const tooltip = this._findTooltipFor(m.id);
        if (!tooltip) return;
        const visEl = tooltip.querySelector('[data-visitors]');
        if (visEl) visEl.textContent = String(m.visitors);
        const trendEl = tooltip.querySelector('[data-trend]');
        if (trendEl) {
          trendEl.textContent = this._formatTrend(m.trend);
          this._setTrendClass(trendEl, m.trend);
        }
      });
    }, 3000);
  }

  _findTooltipFor(id) {
    const wrapper = this._canvas.parentElement;
    if (!wrapper) return null;
    return wrapper.querySelector(`.gsf-globe__tooltip[data-marker-id="${id}"]`);
  }

  _setupCanvasResize() {
    if (typeof ResizeObserver === 'undefined') return;
    this._sizeObserver = new ResizeObserver(() => {
      const w = this._canvas.offsetWidth;
      if (w > 0 && this._globe) {
        this._globe.update({ width: w, height: w });
      }
    });
    this._sizeObserver.observe(this._canvas);
  }

  _setupViewportPause() {
    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this._inView = entry.isIntersecting;
      });
    }, { threshold: 0 });
    this._intersectionObserver.observe(this);
    this._inView = true;
  }

  _animate() {
    const tick = () => {
      if (!this._isPaused && !this._reducedMotion) {
        this._anim.phi += this._speed;
      }
      this._globe.update({
        phi: this._phi + this._anim.phi + this._dragOffset.phi,
        theta: this._theta + this._dragOffset.theta,
      });
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _setupReveal() {
    const section = this.closest('.gsf-globe');
    if (!section) return;
    const els = section.querySelectorAll('.reveal, .rule--earth');
    if (this._reducedMotion) {
      els.forEach((el) => el.classList.add('visible'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    els.forEach((el) => obs.observe(el));
  }

  _escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }
}

if (!customElements.get('gsf-globe')) {
  customElements.define('gsf-globe', GsfGlobe);
}